import styled, { keyframes, type DefaultTheme } from "styled-components";

// Opacity-only: a login form is a page people hit constantly, not a
// marketing splash — one quick, single-axis fade reads as "ready" rather
// than "performing an entrance."
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const AuthPageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &::after {
    content: "";
    display: none;
    position: absolute;
    right: -70px;
    bottom: -90px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.colors.authGradientStart} 0%,
      rgba(10, 179, 184, 0.08) 50%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;

    @media (max-width: 767px) {
      display: block;
    }
  }

  @media (min-width: 768px) {
    flex-direction: row;
    height: 100vh;
    overflow: hidden;
  }
`;

export const WelcomeSection = styled.div`
  display: flex;
  padding: 3.5rem 1.25rem 1.35rem;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: ${({ theme }) => theme.colors.primaryText};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  text-align: center;
  position: relative;
  z-index: 1;
  overflow: hidden;
  min-height: 0;
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    top: -80px;
    left: -70px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.colors.authGradientStart} 0%,
      rgba(10, 179, 184, 0.1) 45%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;

    @media (min-width: 768px) {
      display: none;
    }
  }

  @media (min-width: 768px) {
    padding: 2rem 5% 2rem 6%;
    text-align: left;
    width: 52%;
    min-width: 0;
    height: 100%;
    min-height: unset;
    justify-content: center;
    flex-shrink: unset;
  }
`;

export const WelcomeTagline = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.secondaryText};
  font-style: italic;
  margin: 0;
  position: absolute;
  top: 1.15rem;
  right: 1.25rem;
  text-align: right;
  line-height: 1.35;
  z-index: 1;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const WelcomeInner = styled.div`
  width: 100%;
  max-width: 22rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    max-width: 42rem;
    align-items: inherit;
  }
`;

export const FormSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 1.5rem 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  border-radius: 24px;
  margin: 0 1.15rem 2.25rem;
  box-shadow: 0 10px 40px ${({ theme }) => theme.colors.shadowMedium};
  overflow: visible;
  min-height: 0;

  @media (min-width: 768px) {
    width: 48%;
    height: 100vh;
    flex: unset;
    margin: 0;
    border-radius: 0;
    padding: 3rem 2.5rem;
    justify-content: flex-start;
    align-items: center;
    box-shadow: none;
    overflow-y: auto;
  }
`;

export const HeroTitle = styled.h1`
  font-size: 2.15rem;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.primaryText};
  letter-spacing: -0.03em;
  line-height: 1.05;
  max-width: none;
  text-align: inherit;
  font-style: normal;

  @media (min-width: 768px) {
    font-size: clamp(2.25rem, 4.6vw, 4rem);
    line-height: 1.02;
  }
`;

export const WelcomeHello = styled.span`
  display: block;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryText};
`;

export const WelcomeAccent = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

export const WelcomeRule = styled.span`
  display: block;
  width: 44px;
  height: 3px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  margin: 0.65rem auto 0;

  @media (min-width: 768px) {
    width: 68px;
    height: 5px;
    margin: 0.65rem 0 1.15rem;
  }
`;

export const WelcomeHeading = styled.p`
  display: none;
  margin: 0 0 0.3rem;
  font-size: 1.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textDark};
  line-height: 1.25;

  @media (min-width: 768px) {
    display: block;
  }
`;

export const WelcomeDescription = styled.p`
  display: none;
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.secondaryText};
  max-width: 34rem;
  margin: 0;
  line-height: 1.4;
  font-weight: 400;

  @media (min-width: 768px) {
    display: block;
  }
`;

export const HeroImage = styled.img`
  display: none;
  width: 100%;
  max-width: 640px;
  max-height: min(360px, 40vh);
  height: auto;
  object-fit: contain;
  object-position: left center;
  margin: 0.85rem 0 0.85rem;
  flex-shrink: 1;

  @media (min-width: 768px) {
    display: block;
  }
`;

export const FeatureRow = styled.ul`
  display: none;
  list-style: none;
  margin: 0.15rem 0 0;
  padding: 0;
  gap: 1.75rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    display: flex;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  min-width: 4.5rem;
`;

const FEATURE_ICON_TONES: Record<
  "coral" | "blue" | "mint" | "teal",
  {
    color: (theme: DefaultTheme) => string;
    background: (theme: DefaultTheme) => string;
  }
> = {
  coral: {
    color: (theme) => theme.colors.accentHover,
    background: (theme) => theme.colors.accentTint16,
  },
  blue: {
    color: (theme) => theme.colors.blue,
    background: (theme) => theme.colors.blueTint12,
  },
  mint: {
    color: (theme) => theme.colors.primary,
    background: (theme) => theme.colors.authGradientStart,
  },
  teal: {
    color: (theme) => theme.colors.primary,
    background: (theme) => theme.colors.primaryTint15,
  },
};

export const FeatureIcon = styled.span<{ $tone: keyof typeof FEATURE_ICON_TONES }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $tone }) => FEATURE_ICON_TONES[$tone].color(theme)};
  background: ${({ theme, $tone }) => FEATURE_ICON_TONES[$tone].background(theme)};
`;

export const FeatureLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondaryText};
`;

export const TrustedFooter = styled.p`
  display: none;
  margin: 1.1rem 0 0;
  padding-top: 0;
  color: ${({ theme }) => theme.colors.secondaryText};
  font-size: 0.875rem;
  line-height: 1.45;
  max-width: 28rem;
  align-items: center;
  gap: 0.85rem;

  svg {
    display: block;
    width: 108px;
    height: 32px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (min-width: 768px) {
    display: flex;
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  animation: ${fadeIn} 0.25s ease-out;
  padding: 0;

  @media (min-width: 768px) {
    max-width: 480px;
    padding: 0;
    margin-top: auto;
    margin-bottom: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const FormTitle = styled.h2`
  font-size: 1.625rem;
  font-weight: 700;
  margin-bottom: 1.75rem;
  /* teal700, not primary: primary-on-white fails WCAG AA contrast (2.58:1); teal700 passes (5.47:1) */
  color: ${({ theme }) => theme.colors.teal700};
  text-align: center;
  letter-spacing: -0.3px;

  @media (max-width: 767px) {
    font-size: 1.5rem;
    margin: 0 0 1.15rem;
  }

  @media (min-width: 768px) {
    font-size: 1.875rem;
    margin-bottom: 2.25rem;
  }
`;

export const FormBrand = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 1.15rem;

  @media (min-width: 768px) {
    margin: 0 0 1.5rem;
  }
`;

// Visually hides content while keeping it in the accessibility tree —
// gives screen readers a "Login" heading to match Signup's visible <FormTitle>,
// without duplicating the FormBrand logo's alt text as a page title.
export const VisuallyHidden = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const FormBrandLockup = styled.img`
  height: 40px;
  width: auto;
  max-width: 100%;
  display: block;
  object-fit: contain;

  @media (min-width: 768px) {
    height: 44px;
  }
`;

export const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;

  @media (max-width: 767px) {
    margin-bottom: 1rem;
  }
`;

export const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const PasswordToggleButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.slate500};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.teal600};
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textSlate};
  font-weight: 600;
  letter-spacing: 0.025em;
`;

export const Input = styled.input<{ $hasLeadingIcon?: boolean }>`
  width: 100%;
  padding: 0.95rem 1rem;
  padding-left: ${({ $hasLeadingIcon }) => ($hasLeadingIcon ? "54px" : "1rem")};
  border: 1px solid transparent;
  border-radius: 16px;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-height: 52px;
  background: ${({ theme }) => theme.colors.surfaceLavenderBlue};
  color: ${({ theme }) => theme.colors.textDark};
  font-weight: 500;

  @media (min-width: 768px) {
    padding: 1.25rem;
    padding-left: ${({ $hasLeadingIcon }) => ($hasLeadingIcon ? "54px" : "1.25rem")};
    font-size: 1.0625rem;
    min-height: auto;
    border: 2px solid ${({ theme }) => theme.colors.gradientLight[1]};
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.white};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.teal600};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.tealFocusRing};
    background: ${({ theme }) => theme.colors.white};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.grey300};
  }

  &::placeholder {
    /* slate500, not grey400: grey400 placeholder contrast is ~2.4:1, hard to read */
    color: ${({ theme }) => theme.colors.slate500};
    font-weight: 400;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 0.95rem;
  /* teal700, not primary: white-on-primary fails WCAG AA contrast (2.58:1);
     white-on-teal700 passes (5.47:1) and keeps white button text */
  background: ${({ theme }) => theme.colors.teal700};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.85rem;
  min-height: 52px;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  box-shadow: none;

  @media (min-width: 768px) {
    padding: 1.25rem;
    font-size: 1.0625rem;
    margin-top: 1.25rem;
    min-height: auto;
    border-radius: 12px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.grey300};
    cursor: not-allowed;
    transform: none;
  }
`;

export const Message = styled.p<{ isError?: boolean; isSuccess?: boolean }>`
  /* red700, not red600: red600-on-messageErrorBg fails WCAG AA contrast (4.41:1); red700 passes (4.55:1) */
  color: ${(props) =>
    props.isError
      ? props.theme.colors.red700
      : props.isSuccess
        ? props.theme.colors.green
        : props.theme.colors.slate600};
  background-color: ${(props) =>
    props.isError
      ? props.theme.colors.messageErrorBg
      : props.isSuccess
        ? props.theme.colors.messageSuccessBg
        : props.theme.colors.grey50};
  padding: 1rem;
  border-radius: 12px;
  margin: 1.5rem 0;
  font-size: 0.9375rem;
  text-align: center;
  animation: ${fadeIn} 0.25s ease-out;
  border: 1px solid
    ${(props) =>
      props.isError
        ? props.theme.colors.messageErrorBorder
        : props.isSuccess
          ? props.theme.colors.greenBorderLight
          : props.theme.colors.gradientLight[1]};
  font-weight: 500;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ToggleButton = styled.button`
  background: none;
  border: none;
  /* teal700, not teal600: teal600-on-white fails WCAG AA contrast (3.74:1); teal700 passes (5.47:1) */
  color: ${({ theme }) => theme.colors.teal700};
  cursor: pointer;
  font-size: 0.8125rem;
  margin: 1rem auto 0;
  text-decoration: none;
  font-weight: 400;
  transition: all 0.2s ease;
  display: block;
  text-align: center;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

export const ForgotPasswordLink = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  padding: 0;
  width: 100%;
  font-family: inherit;
  /* teal700, not teal600: teal600-on-white fails WCAG AA contrast (3.74:1); teal700 passes (5.47:1) */
  color: ${({ theme }) => theme.colors.teal700};
  cursor: pointer;
  font-size: 0.8125rem;
  margin-top: 0.5rem;
  text-decoration: none;
  display: block;
  text-align: right;
  font-weight: 400;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

export const RecaptchaContainer = styled.div`
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;

  @media (max-width: 767px) {
    transform: scale(0.9);
    transform-origin: center;
    margin: 1.25rem 0;
  }

  @media (max-width: 480px) {
    transform: scale(0.8);
    transform-origin: center;
    margin: 1rem 0;
  }
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.teal600};
  cursor: pointer;
  font-size: 0.9375rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.teal700};
    text-decoration: underline;
  }

  svg {
    font-size: 1.25rem;
  }
`;

// Same layout as PasswordInputWrapper (relative-positioned flex row for an
// absolutely-positioned adornment) — aliased rather than redefined so the two
// don't drift apart while still giving each input type its own import name.
export const InputWithIconWrapper = PasswordInputWrapper;

export const InputLeftIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  background: none;
  color: ${({ theme }) => theme.colors.secondaryText};
  pointer-events: none;
  z-index: 1;

  svg {
    font-size: 1.15rem;
  }

  @media (min-width: 768px) {
    left: 14px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.gradientLight[0]};

    svg {
      font-size: 1.1rem;
    }
  }
`;

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 0.75rem 0 0;
  gap: 1rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.gradientLight[1]};
  }

  span {
    color: ${({ theme }) => theme.colors.secondaryText};
    font-size: 0.875rem;
    font-weight: 400;
  }
`;

export const SignUpHighlight = styled.span`
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
`;
